/**
 * The seat a tab walks back into after a reload.
 *
 * A [rejoinToken](./rejoinToken.ts) is what a returning player proves its seat with, and until now
 * it lived in memory for the length of one page. That covers a connection that drops and recovers
 * under a page that never stopped running, and nothing else: a player who reloads arrives as a
 * stranger, takes a fresh seat and leaves their old one held against a token nobody will ever
 * present again.
 *
 * So the token is written down, under two scopes and no more.
 *
 * It is scoped to the join code, because a token is a secret about one session. A record filed
 * under `RUST7F2K` is never offered to a room opened on another code, which is what stops a stale
 * token being presented to a session it was never part of.
 *
 * It is scoped to the tab, by living in `sessionStorage`. See
 * [tabTokenStore](./tabTokenStore.ts) for why that scope is the safety argument rather than an
 * implementation detail.
 *
 * There is no expiry stamp, on purpose. The only clock that knows when a seat became reclaimable is
 * the host's, which starts counting `SEAT_HOLD_MS` from the moment the connection dropped, and this
 * side cannot see that moment: a stamp put down when the token was written would expire while its
 * owner was still sitting in the lobby, which is the bug this file exists to fix. A token that is
 * past its welcome is refused by `reclaim` on the host, where the drop is actually known.
 *
 * A code that is not a join code is not filed at all. The loopback reports `LOCAL`, so the
 * development panel and every test that drives a pair in memory write nothing to a real browser.
 */

import { LOBBY_TOKEN_BYTES } from '@/net/wire/lobbyFrame';
import { parseJoinCode } from '@/net/session/joinCode';
import { tabTokenStore } from '@/net/session/tabTokenStore';

/** Three string methods, so the pure half of this file never meets a browser. */
export interface CarriedTokenStore {
  read(key: string): string | null;
  write(key: string, value: string): void;
  remove(key: string): void;
}

export interface CarriedToken {
  /** The token this tab was seated with on this code, or null when there is nothing to present. */
  recall(code: string): string | null;
  /** Writes down the token the host just handed out, so the next page can present it. */
  keep(code: string, token: string): void;
  /** Drops it, for a player who left this session rather than lost it. */
  forget(code: string): void;
}

/** Namespaced, because a tab's storage is shared with everything else this origin ever writes. */
const KEY_PREFIX = 'tbd-seat:';

/** What `mintRejoinToken` produces: the eight bytes as lowercase hex, and nothing else. */
const TOKEN_SHAPE = new RegExp(`^[0-9a-f]{${String(LOBBY_TOKEN_BYTES * 2)}}$`);

export function createCarriedToken(store: CarriedTokenStore = tabTokenStore()): CarriedToken {
  return {
    recall(code: string): string | null {
      const key = keyOf(code);
      if (key === null) return null;
      const found = store.read(key);
      // Storage is a string a person can edit. A token that is not the shape this build mints is
      // one the encoder would turn into eight nonsense bytes, so it never reaches the wire.
      if (found === null || !TOKEN_SHAPE.test(found)) return null;
      return found;
    },

    keep(code: string, token: string): void {
      const key = keyOf(code);
      if (key === null || !TOKEN_SHAPE.test(token)) return;
      store.write(key, token);
    },

    forget(code: string): void {
      const key = keyOf(code);
      if (key === null) return;
      store.remove(key);
    },
  };
}

/**
 * The canonical code, keyed.
 *
 * A transport reports its code grouped for reading aloud, a player types it however they like, and
 * both have to reach the same record: `RUST-7F2K` and `rust7f2k` are one session.
 */
function keyOf(code: string): string | null {
  const parsed = parseJoinCode(code);
  if (!parsed.ok) return null;
  return `${KEY_PREFIX}${parsed.canonical}`;
}
